<template>
<layout-content>
  <template #toolbar>
    <data-time-query :query="query" />

    <el-select
      v-model="query.siteIds[0]"
      size="small"
      @change="onSearchBtnClick"
    >
      <el-option
        v-for="site in filterSites"
        :key="site.id"
        :value="site.id"
        :label="site.name"
      />
    </el-select>

    <el-button
      :disabled="!query.siteIds[0]"
      type="orange"
      size="small"
      icon="el-icon-search"
      @click="onSearchBtnClick"
    >查询</el-button>
  </template>

  <template #body>
    <data-wrapper>
      <el-table
        ref="table"
        :data="dataList"
        border
        stripe
        highlight-current-row
        header-row-class-name="custom-table-header"
        height="100%"
        class="custom-table no-inner-border"
        style="width: 100%"
        @row-click="onTableRowClick"
      >
        <el-table-column prop="siteName" label="站点" />
        <el-table-column prop="dataTimeRange" label="时间" width="310" />
        <el-table-column prop="factorNames" label="因子" />
        <el-table-column prop="dataTypeName" label="数据类型" width="100" />
        <el-table-column prop="auditTypeName" label="审核类型" width="100" />
      </el-table>
    </data-wrapper>
  </template>

  <template #editor>
    <HistoryDetailView
      ref="editor"
      :condition="condition"
      :audit-types="auditTypes"
      :data-types="dataTypes"
      :sites="filterSites"
      :site-factor-map="siteFactorMap"
      :step-data="stepData"
      :source-type="type"
      @closed="onEditorClosed"
    />
  </template>
</layout-content>
</template>

<script>
import BaseSiteDeviceData from '../../BaseSiteDeviceData'
import DeviceCategoryConfig from '../../../model/DeviceCategoryConfig'

import LayoutContent from '../../../components/Layout/Content'
import DataTimeQuery from '../../../components/Data/Query/DataTimeQuery'
import DataWrapper from '../../../components/Data/DataWrapper'
import HistoryDetailView from './HistoryDetailView'

const FactorTypeConfigs = [[
  new DeviceCategoryConfig(1, true),
  new DeviceCategoryConfig(process.env.PM_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.reduce((list, siteDeviceFactor) => {
    if (siteDeviceFactor.factorId.match(/^1008$/)) {
      list.push(siteDeviceFactor)
    }
    return list
  }, [])),
  new DeviceCategoryConfig(process.env.OCEC_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.reduce((list, siteDeviceFactor) => {
    if (siteDeviceFactor.factorId.match(/^64(00|01|04)$/)) {
      list.push(siteDeviceFactor)
    }
    return list
  }, [])),
  new DeviceCategoryConfig(process.env.ION_DEVICE_CATEGORY, false, siteDeviceFactors => siteDeviceFactors.reduce((list, siteDeviceFactor) => {
    const factorId = Number(siteDeviceFactor.factorId)

    if (factorId >= 6100 && factorId <= 6112) {
      list.push(siteDeviceFactor)
    }

    return list
  }, [])),
  new DeviceCategoryConfig(process.env.HEAVY_METAL_DEVICE_CATEGORY, false),
  new DeviceCategoryConfig(process.env.GONG_DEVICE_CATEGORY, false)
], [
  new DeviceCategoryConfig(process.env.VOCS_DEVICE_CATEGORY, true)
]]

const auditTypeMap = {
  1: {
    value: 1,
    label: '原始数据'
  },
  2: {
    value: 2,
    label: '审核数据'
  },
  3: {
    value: 3,
    label: '混合'
  }
}

const dataTypeMap = {
  60: {
    value: 60,
    label: '小时均值',
    formatter: 'YYYY年MM月DD日HH时'
  },
  1440: {
    value: 1440,
    label: '日均值',
    formatter: 'YYYY年MM月DD日'
  }
}

export default {
  name: 'HistoryView',
  extends: BaseSiteDeviceData,
  components: {HistoryDetailView, DataWrapper, DataTimeQuery, LayoutContent},
  props: {
    type: {
      required: true,
      type: Number
    }
  },
  data: () => ({
    pollutantSources: Object.freeze([]),
    condition: null,
    stepData: null
  }),
  computed: {
    queryTimeStep: () => 8,
    siteDeviceCategoryConfig () {
      return FactorTypeConfigs[this.type]
    },
    auditTypes: () => Object.values(auditTypeMap),
    dataTypes: () => Object.values(dataTypeMap),
    siteFactorMap () {
      let {factorMap} = this

      return this.filterSites.reduce((map, site) => {
        let siteId = site.id
        map[siteId] = {
          id: siteId,
          name: site.name,
          map: site.devices.reduce((map, {dataFrequency, maxDelayTime, siteDeviceFactors}) => siteDeviceFactors.reduce((map, deviceFactor) => {
            let {factorId} = deviceFactor
            let factor = factorMap[factorId]

            if (factor) {
              map[factorId] = {
                ...factor,
                dataFrequency,
                maxDelayTime
              }
            }

            return map
          }, map), {})
        }

        return map
      }, {})
    }
  },
  methods: {
    setCurrentQuery () {
      const {query} = this
      this.currentQuery = Object.freeze({
        beginTime: query.beginTime,
        endTime: query.endTime,
        siteId: query.siteIds[0]
      })
    },
    getData () {
      this.changeLoadStatus(true)

      this.$api.PMFAnalysis.getLog(this.currentQuery).then(this.setDataList).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    },
    setDataList (list) {
      const {factorMap, profileSiteMap} = this
      this.dataList = Object.freeze(list.map(data => Object.freeze({
        id: data.id,
        createTime: new Date(data.createTime).format(),
        beginTime: new Date(data.beginTime).format(),
        endTime: new Date(data.endTime).format(),
        dataTimeRange: `${new Date(data.beginTime).format(dataTypeMap[data.dataType].formatter)}~${new Date(data.endTime).format(dataTypeMap[data.dataType].formatter)}`,
        siteId: data.siteId,
        siteName: profileSiteMap[data.siteId].name,
        auditType: data.auditType,
        auditTypeName: auditTypeMap[data.auditType].label,
        dataType: data.dataType,
        dataTypeName: dataTypeMap[data.dataType].label,
        factorIds: data.factorIds,
        factorNames: data.factorIds.map(factorId => factorMap[factorId].name).join(';')
      })))
    },
    onTableRowClick (row) {
      this.condition = row
      this.stepData = {
        task: {
          id: row.id
        },
        results: null
      }

      this.$refs.editor.open()
    },
    onEditorClosed () {
      this.condition = null
      this.stepData = null
    }
  },
  created () {
    this.changeLoadStatus(true)

    this.$api.PMFPollutantSource.getList({
      sourceType: this.type
    }).then(pollutantSources => {
      this.pollutantSources = Object.freeze(pollutantSources)
    }).catch(console.error).finally(() => {
      this.changeLoadStatus(false)
    })
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/11PMF-Analysis/HistoryView.vue